import { Controller } from "stimulus";
import { MOBILE_QUERY } from "../../training_grooming_calendar/url_state";
import {
  DEFAULT_SORT,
  SORT_COLUMNS,
  ariaSort,
  compareKey,
  createCompareCache,
  createCompareLoader,
  keepView,
  matchesQuery,
  nextSort,
  parseSortValue,
  scopeChanged,
  sortRows,
} from "../../training_grooming_calendar/fill_rate_compare";

const CALENDAR = "data-training-grooming-calendar--calendar";
const NOUNS = { classes: "services", slots: "time slots", providers: "providers", rooms: "rooms" };
const SORT_ICONS = { none: "fa-sort", ascending: "fa-arrow-up", descending: "fa-arrow-down" };

export default class extends Controller {
  static values = { url: String };

  static targets = ["compareBody", "search"];

  connect() {
    this.panel = "attention";
    this.mode = "classes";
    this.sortState = DEFAULT_SORT;
    this.query = "";
    this.openRows = new Set();
    this.media = window.matchMedia(MOBILE_QUERY);
    this.cache = createCompareCache();
    this.loader = createCompareLoader({ cache: this.cache, load: (key, signal) => this.fetchCompare(key, signal) });
  }

  disconnect() {
    this.loader.forget();
  }

  rendered() {
    this.loader.forget();
    this.cache.clear();
    const page = this.page();
    if (!page) return;
    if (scopeChanged(this.lastScope, page.dataset.frScope)) {
      this.query = "";
      this.openRows.clear();
    }
    this.lastScope = page.dataset.frScope;
    const available = [...this.element.querySelectorAll(".tg-fr-views [data-view]")].map((tab) => tab.dataset.view);
    ({ panel: this.panel, mode: this.mode } = keepView({ panel: this.panel, mode: this.mode }, available));
    this.centerTimeline();
    this.applyPanel();
    this.applyMode();
    if (this.hasSearchTarget) this.searchTarget.value = this.query;
    this.applyHeaders();
    if (this.panel === "compare") this.loadCompare();
  }

  showView(event) {
    const view = event.currentTarget.dataset.view;
    this.panel = view === "attention" ? "attention" : "compare";
    if (this.panel === "compare") {
      this.mode = view;
      this.query = "";
      this.openRows.clear();
      if (this.hasSearchTarget) this.searchTarget.value = "";
      this.applyMode();
    }
    this.applyPanel();
    this.loadCompare();
  }

  sort(event) {
    this.sortState = nextSort(this.sortState, event.currentTarget.dataset.sort);
    this.applyHeaders();
    this.applyRows();
  }

  sortBy(event) {
    const [column, direction] = event.currentTarget.value.split(":");
    if (!SORT_COLUMNS.includes(column)) return;
    this.sortState = { column, direction: direction === "asc" ? "asc" : "desc" };
    this.applyHeaders();
    this.applyRows();
  }

  search(event) {
    this.query = event.currentTarget.value;
    this.applyRows();
  }

  toggleRow(event) {
    const button = event.currentTarget;
    const id = button.getAttribute("aria-controls");
    const open = button.getAttribute("aria-expanded") !== "true";
    open ? this.openRows.add(id) : this.openRows.delete(id);
    this.setRowOpen(button, open);
  }

  showAll(event) {
    const button = event.currentTarget;
    const group = button.closest(".tg-fr-group");
    if (group) group.querySelectorAll("[data-fr-extra]").forEach((row) => (row.hidden = false));
    button.remove();
  }

  pickFacility(event) {
    const option = event.currentTarget;
    const picker = option.closest(".tg-facility");
    if (picker) {
      picker.querySelectorAll("[role='option']").forEach((node) => node.setAttribute("aria-selected", node === option ? "true" : "false"));
      const value = picker.querySelector(".tg-facility-value");
      const name = option.querySelector(".tg-facility-option-name");
      if (value && name) value.textContent = name.textContent;
      const button = picker.querySelector(".tg-facility-button[aria-label]");
      if (button && name) button.setAttribute("aria-label", `Facility: ${name.textContent.trim()}`);
      const facility = this.application.getControllerForElementAndIdentifier(picker, "training-grooming-calendar--facility");
      if (facility) facility.close();
    }
    option.dispatchEvent(new CustomEvent("training-grooming-filters:change", { bubbles: true, detail: { facility: option.dataset.value } }));
  }

  page() {
    return this.element.querySelector(".tg-fr");
  }

  calendarState() {
    const read = (name) => this.element.getAttribute(`${CALENDAR}-${name}-value`) || "";
    return { date: read("date"), view: read("view"), facility: read("facility") || "all", threshold: read("threshold") || "50", scope: read("scope") || "group" };
  }

  currentKey() {
    return compareKey(this.calendarState(), { mode: this.mode, mobile: this.media.matches });
  }

  fetchCompare(key, signal) {
    return fetch(`${this.urlValue}?${key}`, { headers: { "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin", signal }).then((response) => {
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      return response.text();
    });
  }

  loadCompare() {
    if (!this.hasCompareBodyTarget || this.panel !== "compare") return;
    const key = this.currentKey();
    if (!this.cache.has(key)) this.showSkeleton();
    this.loader
      .request(key)
      .then((result) => {
        if (!result || !this.hasCompareBodyTarget) return;
        this.compareBodyTarget.innerHTML = result.html;
        this.compareBodyTarget.removeAttribute("aria-busy");
        this.afterCompare();
      })
      .catch(() => {
        if (key !== this.loader.currentKey || !this.hasCompareBodyTarget) return;
        this.compareBodyTarget.removeAttribute("aria-busy");
        this.compareBodyTarget.innerHTML = '<div class="tg-error" role="alert">Compare could not be loaded. Please try again.</div>';
      });
  }

  showSkeleton() {
    const template = this.element.querySelector("template[data-fr-skeleton]");
    if (!template) return;
    this.compareBodyTarget.innerHTML = template.innerHTML;
    this.compareBodyTarget.setAttribute("aria-busy", "true");
    const label = this.compareBodyTarget.querySelector("[data-fr-loading-label]");
    if (label) label.textContent = `Loading ${NOUNS[this.mode] || "services"}…`;
  }

  afterCompare() {
    this.compareBodyTarget.querySelectorAll("button.tg-fr-crow-main[aria-controls]").forEach((button) => {
      this.setRowOpen(button, this.openRows.has(button.getAttribute("aria-controls")));
    });
    this.applyHeaders();
    this.applyRows();
  }

  centerTimeline() {
    const bars = this.element.querySelector(".tg-fr-bars");
    const selected = bars && bars.querySelector(".tg-fr-bar-selected");
    if (!selected || bars.scrollWidth <= bars.clientWidth) return;
    bars.scrollLeft = selected.offsetLeft - (bars.clientWidth - selected.offsetWidth) / 2;
  }

  setRowOpen(button, open) {
    button.setAttribute("aria-expanded", open ? "true" : "false");
    const row = button.closest(".tg-fr-crow");
    const children = row && row.querySelector(":scope > .tg-fr-crow-children");
    if (children) children.hidden = !open;
  }

  applyPanel() {
    const view = this.panel === "attention" ? "attention" : this.mode;
    this.element.querySelectorAll(".tg-fr-views [data-view]").forEach((tab) => {
      const active = tab.dataset.view === view;
      tab.classList.toggle("active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });
    this.element.querySelectorAll("[data-fr-view-title]").forEach((title) => (title.hidden = title.dataset.frViewTitle !== view));
    this.element.querySelectorAll("[data-fr-panel]").forEach((panel) => (panel.hidden = panel.dataset.frPanel !== this.panel));
    const compare = this.element.querySelector("#tg-fr-panel-compare");
    if (compare) compare.setAttribute("aria-labelledby", `tg-fr-tab-${this.mode}`);
  }

  applyMode() {
    const grid = this.mode === "slots";
    this.element.querySelectorAll(".tg-fr-compare-bar").forEach((node) => (node.hidden = grid));
    this.element.querySelectorAll("[data-fr-compare-head]").forEach((node) => (node.hidden = grid));
    this.element.querySelectorAll(".tg-fr-sort").forEach((button) => {
      const label = button.dataset[`label${this.mode.charAt(0).toUpperCase()}${this.mode.slice(1)}`];
      const text = button.querySelector(".tg-fr-sort-label");
      if (label && text) text.textContent = label;
    });
  }

  applyHeaders() {
    this.element.querySelectorAll(".tg-fr-sort[data-sort]").forEach((button) => {
      const state = ariaSort(this.sortState, button.dataset.sort);
      button.setAttribute("aria-sort", state);
      const icon = button.querySelector(".fa");
      if (icon) {
        Object.values(SORT_ICONS).forEach((name) => icon.classList.remove(name));
        icon.classList.add(SORT_ICONS[state]);
      }
    });
    this.element.querySelectorAll("[data-fr-sort-select]").forEach((select) => (select.value = `${this.sortState.column}:${this.sortState.direction}`));
  }

  applyRows() {
    if (!this.hasCompareBodyTarget) return;
    const containers = [...this.compareBodyTarget.querySelectorAll("[data-fr-rows]")];
    if (!containers.length) return;
    const rows = containers.flatMap((container) => [...container.children].map((element) => ({
      container,
      element,
      pinned: element.dataset.frPinLast === "true",
      name: element.dataset.sortName,
      sub: element.dataset.frSub,
      values: Object.fromEntries(SORT_COLUMNS.map((column) => [column, parseSortValue(column, element.dataset[`sort${column.charAt(0).toUpperCase()}${column.slice(1)}`])])),
    })));
    sortRows(rows, this.sortState).forEach((row) => row.container.appendChild(row.element));
    let shown = 0;
    rows.forEach((row) => {
      const match = matchesQuery(row, this.query);
      row.element.hidden = !match;
      if (match) shown += 1;
    });
    const empty = this.compareBodyTarget.querySelector("[data-fr-no-match]");
    if (empty) empty.hidden = shown > 0 || rows.length === 0;
  }
}
